import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogOut, Home, Plus, Trash2, Edit, ExternalLink } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { BAR_STRUCTURE, BISTRO_STRUCTURE, BAR_TILES, BISTRO_TILES, BISTRO_LOCKED_TILES } from "@/lib/carteStructures";

const JOURS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("plats");

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard">
      <Toaster position="top-right" richColors />
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/images/logo.png" alt="" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-serif text-2xl text-white">Admin · Le Bistrot de la Neuve</div>
              <div className="text-xs text-white/60 uppercase tracking-[0.25em]">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/70 hover:text-white">
              <Home size={14} /> Voir le site
            </Link>
            <Button
              onClick={logout}
              data-testid="logout-btn"
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0A0A0A] rounded-none"
            >
              <LogOut size={14} className="mr-2" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-[#E5E0D8] rounded-none h-auto p-0 flex-wrap">
            {[
              ["plats", "Plats du jour"],
              ["formules", "Formule Saison"],
              ["menu-enfant", "Menu Enfant"],
              ["boissons", "Boissons"],
              ["carte-bistro", "Carte Bistrot"],
              ["carte-bar", "Carte Bar"],
              ["horaires", "Horaires"],
              ["popups", "Popups"],
            ].map(([v, l]) => (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={`tab-${v}`}
                className="data-[state=active]:bg-[#A61C24] data-[state=active]:text-white rounded-none px-5 py-3 text-xs uppercase tracking-[0.2em]"
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="plats" className="mt-8"><PlatsManager /></TabsContent>
          <TabsContent value="formules" className="mt-8"><FormulesManager /></TabsContent>
          <TabsContent value="menu-enfant" className="mt-8"><MenuEnfantManager /></TabsContent>
          <TabsContent value="boissons" className="mt-8"><BoissonsManager /></TabsContent>
          <TabsContent value="carte-bistro" className="mt-8"><CarteManager carte="bistro" /></TabsContent>
          <TabsContent value="carte-bar" className="mt-8"><CarteManager carte="bar" /></TabsContent>
          <TabsContent value="horaires" className="mt-8"><HorairesManager /></TabsContent>
          <TabsContent value="popups" className="mt-8"><PopupsManager /></TabsContent>
        </Tabs>

        <div className="mt-10 flex gap-4 text-sm">
          <Link to="/carte-bistro" target="_blank" className="inline-flex items-center gap-2 text-[#A61C24] hover:underline" data-testid="open-carte-bistro">
            <ExternalLink size={14} /> Ouvrir carte bistrot (tablette)
          </Link>
          <Link to="/carte-bar" target="_blank" className="inline-flex items-center gap-2 text-[#A61C24] hover:underline" data-testid="open-carte-bar">
            <ExternalLink size={14} /> Ouvrir carte bar (tablette)
          </Link>
        </div>
      </div>
    </div>
  );
}

// ---------------------- Generic helpers ----------------------
function SectionHeader({ title, subtitle, onAdd }) {
  return (
    <div className="flex justify-between items-start mb-6">
      <div>
        <h2 className="font-serif text-3xl text-[#1A1A1A]">{title}</h2>
        {subtitle && <p className="text-sm text-[#1A1A1A]/60 mt-1">{subtitle}</p>}
      </div>
      {onAdd && (
        <Button onClick={onAdd} data-testid={`add-btn-${title}`} className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">
          <Plus size={16} className="mr-2" /> Ajouter
        </Button>
      )}
    </div>
  );
}

function ActiveBadge({ actif }) {
  return (
    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${actif ? "bg-[#A61C24]/10 text-[#A61C24]" : "bg-[#E5E0D8] text-[#1A1A1A]/50"}`}>
      {actif ? "Actif" : "Inactif"}
    </span>
  );
}

function ItemRow({ children, onEdit, onDelete, testId }) {
  return (
    <div className="border border-[#E5E0D8] bg-white p-5 flex items-start gap-4" data-testid={testId}>
      <div className="flex-1">{children}</div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onEdit} className="rounded-none">
          <Edit size={14} />
        </Button>
        <Button size="sm" variant="outline" onClick={onDelete} className="rounded-none text-[#A61C24] border-[#A61C24]/30 hover:bg-[#A61C24] hover:text-white">
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
}

// ---------------------- PLATS DU JOUR ----------------------
function PlatsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // existing plat object OR { jour } for new

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/plats-jour");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Map: jour -> plat (one per day max, we use the first if multiple exist)
  const byJour = useMemo(() => {
    const m = {};
    for (const p of items) { if (!m[p.jour]) m[p.jour] = p; }
    return m;
  }, [items]);

  return (
    <div>
      <SectionHeader
        title="Plats du jour"
        subtitle="Un plat par jour. Les 7 jours sont figés : vous ne pouvez qu'en modifier le contenu."
      />
      <div className="space-y-3">
        {JOURS.map((jour) => {
          const p = byJour[jour];
          return (
            <div
              key={jour}
              data-testid={`plat-row-${jour}`}
              className="border border-[#E5E0D8] bg-white p-5 flex items-start gap-4"
            >
              <span className="text-[10px] uppercase tracking-[0.25em] bg-[#1A1A1A] text-white px-3 py-1.5 self-start min-w-[90px] text-center">
                {jour}
              </span>
              <div className="flex-1 min-w-0">
                {p ? (
                  <>
                    <div className="flex items-start gap-3 flex-wrap">
                      <ActiveBadge actif={p.actif} />
                      <h3 className="font-serif text-xl text-[#1A1A1A]">{p.nom || <span className="italic text-[#1A1A1A]/40">– sans nom –</span>}</h3>
                      {p.prix != null && <span className="price-tag text-[#A61C24]">{p.prix.toFixed(2)} €</span>}
                    </div>
                    {p.description && <p className="text-sm text-[#1A1A1A]/70 mt-1">{p.description}</p>}
                  </>
                ) : (
                  <p className="text-sm italic text-[#1A1A1A]/50">Aucun plat défini pour ce jour.</p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setEditing(p ? p : { jour }); setOpen(true); }}
                className="rounded-none"
                data-testid={`edit-plat-${jour}`}
              >
                <Edit size={14} className="mr-2" /> {p ? "Modifier" : "Définir"}
              </Button>
            </div>
          );
        })}
      </div>
      <PlatEditor open={open} onClose={() => setOpen(false)} item={editing} onSaved={() => { setOpen(false); load(); }} />

      <div className="mt-16">
        <MenuExpressSection />
      </div>
    </div>
  );
}

function MenuExpressSection() {
  const [form, setForm] = useState({});
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/menu-express"); setForm(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = {
        entree_1: form.entree_1 || "",
        entree_2: form.entree_2 || "",
        prix_entree: form.prix_entree === "" || form.prix_entree == null ? null : Number(form.prix_entree),
        dessert_1: form.dessert_1 || "",
        dessert_2: form.dessert_2 || "",
        prix_dessert: form.prix_dessert === "" || form.prix_dessert == null ? null : Number(form.prix_dessert),
      };
      await api.put("/admin/menu-express", payload);
      toast.success("Menu express enregistré");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="menu-express-section">
      <div className="mb-4 pb-3 border-b border-[#E5E0D8]">
        <h2 className="font-serif text-2xl text-[#1A1A1A]">Entrées &amp; Desserts du jour (Formule Express)</h2>
        <p className="text-sm text-[#1A1A1A]/60 mt-1">Affichés uniquement sur la carte tablette du bistrot.</p>
      </div>
      <div className="bg-white border border-[#E5E0D8] p-6 space-y-6 max-w-3xl">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A61C24] mb-2">Entrées du jour</div>
          <div className="grid md:grid-cols-[1fr_1fr_120px] gap-3">
            <Input placeholder="Entrée 1" value={form.entree_1 || ""} onChange={(e) => set("entree_1", e.target.value)} className="rounded-none" />
            <Input placeholder="Entrée 2" value={form.entree_2 || ""} onChange={(e) => set("entree_2", e.target.value)} className="rounded-none" />
            <Input type="number" step="0.5" placeholder="Prix €" value={form.prix_entree ?? ""} onChange={(e) => set("prix_entree", e.target.value)} className="rounded-none" />
          </div>
          <p className="text-xs text-[#1A1A1A]/50 mt-1.5">Le prix est le même pour les deux entrées.</p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A61C24] mb-2">Desserts du jour</div>
          <div className="grid md:grid-cols-[1fr_1fr_120px] gap-3">
            <Input placeholder="Dessert 1" value={form.dessert_1 || ""} onChange={(e) => set("dessert_1", e.target.value)} className="rounded-none" />
            <Input placeholder="Dessert 2" value={form.dessert_2 || ""} onChange={(e) => set("dessert_2", e.target.value)} className="rounded-none" />
            <Input type="number" step="0.5" placeholder="Prix €" value={form.prix_dessert ?? ""} onChange={(e) => set("prix_dessert", e.target.value)} className="rounded-none" />
          </div>
          <p className="text-xs text-[#1A1A1A]/50 mt-1.5">Le prix est le même pour les deux desserts.</p>
        </div>
        <Button onClick={save} data-testid="menu-express-save-btn" className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

function MenuEnfantManager() {
  const [form, setForm] = useState({});
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/menu-enfant"); setForm(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = {
        plat_1: form.plat_1 || "",
        plat_2: form.plat_2 || "",
        dessert_1: form.dessert_1 || "",
        dessert_2: form.dessert_2 || "",
        prix: form.prix === "" || form.prix == null ? null : Number(form.prix),
      };
      await api.put("/admin/menu-enfant", payload);
      toast.success("Menu enfant enregistré");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="menu-enfant-manager">
      <SectionHeader title="Menu Enfant" subtitle="Affiché dans la carte tablette du bistrot · Le texte « Enfant de moins de 12 ans » est figé." />
      <div className="bg-white border border-[#E5E0D8] p-6 space-y-6 max-w-2xl">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A61C24] mb-2">Plats principaux</div>
          <div className="space-y-3">
            <Input placeholder="Plat 1" value={form.plat_1 || ""} onChange={(e) => set("plat_1", e.target.value)} className="rounded-none" />
            <Input placeholder="Plat 2" value={form.plat_2 || ""} onChange={(e) => set("plat_2", e.target.value)} className="rounded-none" />
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A61C24] mb-2">Desserts</div>
          <div className="space-y-3">
            <Input placeholder="Dessert 1" value={form.dessert_1 || ""} onChange={(e) => set("dessert_1", e.target.value)} className="rounded-none" />
            <Input placeholder="Dessert 2" value={form.dessert_2 || ""} onChange={(e) => set("dessert_2", e.target.value)} className="rounded-none" />
          </div>
        </div>
        <div className="max-w-[160px]">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A61C24] mb-2">Prix du menu (€)</div>
          <Input type="number" step="0.5" value={form.prix ?? ""} onChange={(e) => set("prix", e.target.value)} className="rounded-none" />
        </div>
        <Button onClick={save} data-testid="menu-enfant-save-btn" className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

function PlatEditor({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState({});
  // `item` can be: existing plat (has `id`), OR a placeholder `{ jour }` for creation
  const isExisting = !!(item && item.id);
  const jour = item?.jour || "lundi";

  useEffect(() => {
    if (!open) return;
    setForm(
      isExisting
        ? { ...item }
        : { jour, nom: "", description: "", prix: "", actif: true, ordre: 0 }
    );
  }, [item, open, isExisting, jour]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = {
        jour,
        nom: form.nom || "",
        description: form.description || "",
        prix: form.prix === "" || form.prix == null ? null : Number(form.prix),
        actif: !!form.actif,
        ordre: form.ordre ?? 0,
      };
      if (isExisting) await api.put(`/admin/plats-jour/${item.id}`, payload);
      else await api.post("/admin/plats-jour", payload);
      toast.success("Enregistré");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#FDFBF7] rounded-none max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl capitalize">
            Plat du {jour}
          </DialogTitle>
          <p className="text-xs text-[#1A1A1A]/55 uppercase tracking-[0.25em] mt-1">Le jour est figé</p>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Nom du plat</Label><Input data-testid="plat-nom-input" value={form.nom || ""} onChange={(e) => set("nom", e.target.value)} className="rounded-none" /></div>
          <div><Label>Description</Label><Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-none" /></div>
          <div className="max-w-[160px]">
            <Label>Prix (€)</Label>
            <Input type="number" step="0.5" data-testid="plat-prix-input" value={form.prix ?? ""} onChange={(e) => set("prix", e.target.value)} className="rounded-none" />
          </div>
          <div className="flex items-center gap-3"><Switch checked={!!form.actif} onCheckedChange={(v) => set("actif", v)} data-testid="plat-actif-switch" /><span className="text-sm">Visible sur le site</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          <Button onClick={save} data-testid="plat-save-btn" className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------- FORMULES ----------------------
function FormulesManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/formules");
      setItems(data.filter((f) => f.type === "saison"));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // There should be only ONE formule saison. If several exist (legacy), we edit the first.
  const f = items[0] || null;

  return (
    <div>
      <SectionHeader
        title="Formule de Saison"
        subtitle="Une seule formule saison. Vous ne pouvez modifier que son contenu. La Formule Express est figée et se gère ailleurs."
      />
      {f ? (
        <div
          data-testid={`row-formule-${f.id}`}
          className="border border-[#E5E0D8] bg-white p-6 flex items-start gap-4"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.25em] bg-[#A61C24] text-white px-2 py-1">saison</span>
              <ActiveBadge actif={f.actif} />
              <h3 className="font-serif text-2xl">{f.nom}</h3>
              {f.prix != null && <span className="price-tag text-[#A61C24]">{f.prix.toFixed(2)} €</span>}
            </div>
            {f.description && <p className="text-sm text-[#1A1A1A]/70 mt-2 whitespace-pre-line">{f.description}</p>}
            <div className="text-xs text-[#1A1A1A]/60 mt-3 space-y-0.5">
              {f.entrees?.length > 0 && <div><span className="uppercase tracking-[0.2em] text-[#A61C24] mr-2">Entrées</span>{f.entrees.join(" · ")}</div>}
              {f.plats?.length > 0 && <div><span className="uppercase tracking-[0.2em] text-[#A61C24] mr-2">Plats</span>{f.plats.join(" · ")}</div>}
              {f.desserts?.length > 0 && <div><span className="uppercase tracking-[0.2em] text-[#A61C24] mr-2">Desserts</span>{f.desserts.join(" · ")}</div>}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpen(true)}
            className="rounded-none"
            data-testid="edit-formule-saison"
          >
            <Edit size={14} className="mr-2" /> Modifier
          </Button>
        </div>
      ) : (
        <div className="border border-dashed border-[#A61C24]/40 bg-[#A61C24]/5 p-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-[#1A1A1A]/75">Aucune formule de saison enregistrée.</p>
            <p className="text-xs text-[#1A1A1A]/55 mt-1">Cliquez pour l'initialiser.</p>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none"
            data-testid="init-formule-saison"
          >
            Initialiser
          </Button>
        </div>
      )}
      <FormuleEditor open={open} onClose={() => setOpen(false)} item={f} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function FormuleEditor({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open) return;
    setForm(item
      ? { ...item, entrees: (item.entrees || []).join("\n"), plats: (item.plats || []).join("\n"), desserts: (item.desserts || []).join("\n") }
      : { type: "saison", nom: "Formule de Saison", description: "", prix: "", entrees: "", plats: "", desserts: "", actif: true, ordre: 1 }
    );
  }, [item, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const splitLines = (s) => (s || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const save = async () => {
    try {
      const payload = {
        type: "saison",
        nom: form.nom || "Formule de Saison",
        description: form.description || "",
        prix: form.prix === "" || form.prix == null ? null : Number(form.prix),
        entrees: splitLines(form.entrees),
        plats: splitLines(form.plats),
        desserts: splitLines(form.desserts),
        actif: !!form.actif,
        ordre: item?.ordre ?? 1,
      };
      if (item) await api.put(`/admin/formules/${item.id}`, payload);
      else await api.post("/admin/formules", payload);
      toast.success("Enregistrée"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#FDFBF7] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Formule de Saison</DialogTitle>
          <p className="text-xs text-[#1A1A1A]/55 uppercase tracking-[0.25em] mt-1">Modification du contenu uniquement</p>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Nom</Label><Input value={form.nom || ""} onChange={(e) => set("nom", e.target.value)} className="rounded-none" /></div>
          <div><Label>Description</Label><Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-none" /></div>
          <div className="max-w-[160px]">
            <Label>Prix (€)</Label>
            <Input type="number" step="0.5" value={form.prix ?? ""} onChange={(e) => set("prix", e.target.value)} className="rounded-none" />
          </div>
          <div><Label>Entrées (une par ligne)</Label><Textarea rows={3} value={form.entrees || ""} onChange={(e) => set("entrees", e.target.value)} className="rounded-none" /></div>
          <div><Label>Plats (un par ligne)</Label><Textarea rows={3} value={form.plats || ""} onChange={(e) => set("plats", e.target.value)} className="rounded-none" /></div>
          <div><Label>Desserts (un par ligne)</Label><Textarea rows={3} value={form.desserts || ""} onChange={(e) => set("desserts", e.target.value)} className="rounded-none" /></div>
          <div className="flex items-center gap-3"><Switch checked={!!form.actif} onCheckedChange={(v) => set("actif", v)} /><span className="text-sm">Visible sur le site</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          <Button onClick={save} className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------- BOISSONS ----------------------
function BoissonsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/boissons"); setItems(data); } catch(e){toast.error(formatApiError(e));} }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => { if (!window.confirm("Supprimer ?")) return; try { await api.delete(`/admin/boissons/${id}`); toast.success("Supprimée"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div>
      <SectionHeader title="Boissons" subtitle="Groupées par catégorie" onAdd={() => { setEditing(null); setOpen(true); }} />
      <div className="space-y-3">
        {items.map((b) => (
          <ItemRow key={b.id} testId={`row-boisson-${b.id}`} onEdit={() => { setEditing(b); setOpen(true); }} onDelete={() => onDelete(b.id)}>
            <div className="flex items-start gap-3 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.25em] bg-[#1A1A1A] text-white px-2 py-1">{b.categorie}</span>
              <ActiveBadge actif={b.actif} />
              <h3 className="font-serif text-lg">{b.nom}</h3>
              {b.volume && <span className="text-xs text-[#1A1A1A]/60">{b.volume}</span>}
              {b.prix != null && <span className="price-tag text-[#A61C24]">{b.prix.toFixed(2)} €</span>}
            </div>
          </ItemRow>
        ))}
      </div>
      <BoissonEditor open={open} onClose={() => setOpen(false)} item={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function BoissonEditor({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(item ? { ...item } : { categorie: "Softs", nom: "", description: "", volume: "", prix: "", actif: true, ordre: 0 }); }, [item, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...form, prix: form.prix === "" || form.prix == null ? null : Number(form.prix), ordre: Number(form.ordre) || 0 };
      if (item) await api.put(`/admin/boissons/${item.id}`, payload);
      else await api.post("/admin/boissons", payload);
      toast.success("Enregistrée"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#FDFBF7] rounded-none max-w-xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{item ? "Modifier" : "Nouvelle"} boisson</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Catégorie</Label><Input value={form.categorie || ""} onChange={(e) => set("categorie", e.target.value)} placeholder="Vins rouges, Bières, Softs, Cafés & thés…" className="rounded-none" /></div>
          <div><Label>Nom</Label><Input value={form.nom || ""} onChange={(e) => set("nom", e.target.value)} className="rounded-none" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Volume</Label><Input value={form.volume || ""} onChange={(e) => set("volume", e.target.value)} placeholder="25cl, 75cl" className="rounded-none" /></div>
            <div><Label>Prix (€)</Label><Input type="number" step="0.5" value={form.prix ?? ""} onChange={(e) => set("prix", e.target.value)} className="rounded-none" /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-none" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Ordre</Label><Input type="number" value={form.ordre ?? 0} onChange={(e) => set("ordre", e.target.value)} className="rounded-none" /></div>
            <div className="flex items-center gap-3 mt-6"><Switch checked={!!form.actif} onCheckedChange={(v) => set("actif", v)} /><span className="text-sm">Visible</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          <Button onClick={save} className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------- CARTE (bistro/bar) ----------------------
function CarteManager({ carte }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => { try { const { data } = await api.get(`/admin/cartes/${carte}`); setItems(data); } catch(e){toast.error(formatApiError(e));} }, [carte]);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => { if (!window.confirm("Supprimer ?")) return; try { await api.delete(`/admin/cartes/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const structure = carte === "bar" ? BAR_STRUCTURE : BISTRO_STRUCTURE;
  const title = carte === "bar" ? "Carte Bar" : "Carte Bistrot";

  return (
    <div>
      <SectionHeader
        title={title}
        subtitle={`Affichée sur la tablette /carte-${carte} · catégories figées dans le code`}
        onAdd={() => { setEditing(null); setOpen(true); }}
      />
      <div className="space-y-10">
        {structure.map((cat) => (
          <div key={cat.label} data-testid={`admin-${carte}-cat-${cat.label}`}>
            <h3 className="font-serif text-2xl text-[#A61C24] uppercase tracking-[0.05em] mb-4">{cat.label}</h3>
            <div className="space-y-6 pl-4 border-l-2 border-[#E5E0D8]">
              {cat.tiles.map((tile) => {
                const isLocked = carte === "bistro" && BISTRO_LOCKED_TILES.includes(tile);
                const tileItems = items.filter((it) => it.categorie === tile).sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
                return (
                  <div key={tile} data-testid={`admin-${carte}-tile-${tile}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <h4 className="font-serif text-xl text-[#1A1A1A]">{tile}</h4>
                      {!isLocked && (
                        <span className="text-xs text-[#1A1A1A]/50">
                          {tileItems.length} {tileItems.length > 1 ? "items" : "item"}
                        </span>
                      )}
                    </div>
                    {isLocked ? (
                      <p className="text-sm text-[#1A1A1A]/65 py-3 px-4 bg-[#A61C24]/5 border border-dashed border-[#A61C24]/30">
                        {tile === "Formules (du jour & de saison)" ? (
                          <>Géré via les onglets <strong>Plats du jour</strong> (Formule Express) et <strong>Formule de Saison</strong>.</>
                        ) : (
                          <>Géré via l'onglet <strong>Menu Enfant</strong>.</>
                        )}
                      </p>
                    ) : tileItems.length === 0 ? (
                      <p className="text-sm text-[#1A1A1A]/50 italic py-3 px-4 bg-[#E5E0D8]/30 border border-dashed border-[#E5E0D8]">
                        Aucun item, cliquez sur "Ajouter" pour en créer un.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {tileItems.map((it) => (
                          <ItemRow key={it.id} testId={`row-carte-${it.id}`} onEdit={() => { setEditing(it); setOpen(true); }} onDelete={() => onDelete(it.id)}>
                            <div className="flex items-start gap-3 flex-wrap">
                              <ActiveBadge actif={it.actif} />
                              <h3 className="font-serif text-lg">{it.nom}</h3>
                              {it.sous_categorie && (
                                <span className="text-[10px] uppercase tracking-[0.2em] bg-[#A61C24]/10 text-[#A61C24] px-2 py-0.5">
                                  {it.sous_categorie}
                                </span>
                              )}
                              {it.prix != null && <span className="price-tag text-[#A61C24]">{it.prix.toFixed(2)} €</span>}
                            </div>
                            {it.description && <p className="text-sm text-[#1A1A1A]/70 mt-1">{it.description}</p>}
                          </ItemRow>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <CarteEditor open={open} onClose={() => setOpen(false)} item={editing} carte={carte} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function CarteEditor({ open, onClose, item, carte, onSaved }) {
  const [form, setForm] = useState({});
  const structure = carte === "bar" ? BAR_STRUCTURE : BISTRO_STRUCTURE;
  const tiles = useMemo(
    () =>
      (carte === "bar" ? BAR_TILES : BISTRO_TILES).filter(
        (t) => !(carte === "bistro" && BISTRO_LOCKED_TILES.includes(t))
      ),
    [carte]
  );
  useEffect(() => {
    if (!open) return;
    const initial = item
      ? { ...item }
      : { carte, categorie: tiles[0], sous_categorie: "", nom: "", description: "", prix: "", actif: true, ordre: 0 };
    setForm(initial);
  }, [item, open, carte, tiles]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = {
        ...form,
        carte,
        sous_categorie: (form.sous_categorie || "").trim(),
        prix: form.prix === "" || form.prix == null ? null : Number(form.prix),
        ordre: Number(form.ordre) || 0,
      };
      if (item) await api.put(`/admin/cartes/${item.id}`, payload);
      else await api.post("/admin/cartes", payload);
      toast.success("Enregistré"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#FDFBF7] rounded-none max-w-xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{item ? "Modifier" : "Nouvel"} item ({carte})</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Catégorie</Label>
            <Select value={form.categorie || tiles[0]} onValueChange={(v) => set("categorie", v)}>
              <SelectTrigger data-testid="carte-categorie-select" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {structure.map((cat) => (
                  <div key={cat.label}>
                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.25em] text-[#A61C24] font-semibold">{cat.label}</div>
                    {cat.tiles.filter((t) => !(carte === "bistro" && BISTRO_LOCKED_TILES.includes(t))).map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nom</Label><Input value={form.nom || ""} onChange={(e) => set("nom", e.target.value)} className="rounded-none" /></div>
          <div>
            <Label>Sous-catégorie <span className="text-[#1A1A1A]/45 font-normal normal-case text-xs">(optionnel — regroupe les items sur la tablette, ex : Bouteilles / Pots / Verres)</span></Label>
            <Input
              data-testid="carte-souscategorie-input"
              value={form.sous_categorie || ""}
              onChange={(e) => set("sous_categorie", e.target.value)}
              placeholder="Laisser vide pour ne pas grouper"
              className="rounded-none"
            />
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-none" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Prix (€)</Label><Input type="number" step="0.5" value={form.prix ?? ""} onChange={(e) => set("prix", e.target.value)} className="rounded-none" /></div>
            <div><Label>Ordre</Label><Input type="number" value={form.ordre ?? 0} onChange={(e) => set("ordre", e.target.value)} className="rounded-none" /></div>
          </div>
          <div className="flex items-center gap-3"><Switch checked={!!form.actif} onCheckedChange={(v) => set("actif", v)} /><span className="text-sm">Visible</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          <Button onClick={save} className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------- HORAIRES ----------------------
function HorairesManager() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/horaires"); setItems(data); } catch(e){toast.error(formatApiError(e));} }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (h, patch) => {
    try {
      const payload = { jour: h.jour, ferme: h.ferme, midi_ouverture: h.midi_ouverture, midi_fermeture: h.midi_fermeture, soir_ouverture: h.soir_ouverture, soir_fermeture: h.soir_fermeture, note: h.note, ...patch };
      await api.put(`/admin/horaires/${h.id}`, payload);
      toast.success("Mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <SectionHeader title="Horaires d'ouverture" subtitle="Modifiés en direct" />
      <div className="space-y-3">
        {items.map((h) => (
          <div key={h.id} className="bg-white border border-[#E5E0D8] p-5" data-testid={`horaire-row-${h.jour}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="capitalize font-serif text-xl">{h.jour}</div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={!!h.ferme} onCheckedChange={(v) => update(h, { ferme: v })} />
                <span>Fermé</span>
              </label>
            </div>
            {!h.ferme && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <TimeField label="Midi ouverture" value={h.midi_ouverture} onChange={(v) => update(h, { midi_ouverture: v })} />
                <TimeField label="Midi fermeture" value={h.midi_fermeture} onChange={(v) => update(h, { midi_fermeture: v })} />
                <TimeField label="Soir ouverture" value={h.soir_ouverture} onChange={(v) => update(h, { soir_ouverture: v })} />
                <TimeField label="Soir fermeture" value={h.soir_fermeture} onChange={(v) => update(h, { soir_fermeture: v })} />
              </div>
            )}
            <div className="mt-3">
              <Label className="text-xs">Note</Label>
              <Input defaultValue={h.note || ""} onBlur={(e) => { if (e.target.value !== (h.note || "")) update(h, { note: e.target.value }); }} className="rounded-none" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TimeField({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="time" defaultValue={value || ""} onBlur={(e) => { const v = e.target.value || null; if (v !== value) onChange(v); }} className="rounded-none" />
    </div>
  );
}

// ---------------------- POPUPS ----------------------
function PopupsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/popups"); setItems(data); } catch(e){toast.error(formatApiError(e));} }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => { if (!window.confirm("Supprimer ?")) return; try { await api.delete(`/admin/popups/${id}`); toast.success("Supprimée"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div>
      <SectionHeader title="Popups & annonces" subtitle="Affichés sur la page d'accueil" onAdd={() => { setEditing(null); setOpen(true); }} />
      <div className="space-y-3">
        {items.map((p) => (
          <ItemRow key={p.id} testId={`row-popup-${p.id}`} onEdit={() => { setEditing(p); setOpen(true); }} onDelete={() => onDelete(p.id)}>
            <div className="flex items-center gap-3 mb-1"><ActiveBadge actif={p.actif} /><h3 className="font-serif text-lg">{p.titre}</h3></div>
            <p className="text-sm text-[#1A1A1A]/70 whitespace-pre-wrap">{p.contenu}</p>
          </ItemRow>
        ))}
        {items.length === 0 && <p className="text-sm text-[#1A1A1A]/60">Aucun popup actif.</p>}
      </div>
      <PopupEditor open={open} onClose={() => setOpen(false)} item={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function PopupEditor({ open, onClose, item, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(item ? { ...item } : { titre: "", contenu: "", actif: true, ordre: 0 }); }, [item, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = { titre: form.titre, contenu: form.contenu, actif: !!form.actif, ordre: Number(form.ordre) || 0 };
      if (item) await api.put(`/admin/popups/${item.id}`, payload);
      else await api.post("/admin/popups", payload);
      toast.success("Enregistré"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#FDFBF7] rounded-none max-w-xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{item ? "Modifier" : "Nouveau"} popup</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Titre</Label><Input data-testid="popup-titre-input" value={form.titre || ""} onChange={(e) => set("titre", e.target.value)} className="rounded-none" /></div>
          <div><Label>Contenu</Label><Textarea rows={5} value={form.contenu || ""} onChange={(e) => set("contenu", e.target.value)} className="rounded-none" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Ordre</Label><Input type="number" value={form.ordre ?? 0} onChange={(e) => set("ordre", e.target.value)} className="rounded-none" /></div>
            <div className="flex items-center gap-3 mt-6"><Switch checked={!!form.actif} onCheckedChange={(v) => set("actif", v)} /><span className="text-sm">Actif</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          <Button onClick={save} data-testid="popup-save-btn" className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------- SETTINGS ----------------------
function SettingsManager() {
  const [form, setForm] = useState({ nom: "", adresse: "", telephone: "", email: "", description: "" });
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/settings"); setForm(data); } catch(e){toast.error(formatApiError(e));} }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try { await api.put("/admin/settings", form); toast.success("Enregistré"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <SectionHeader title="Paramètres" subtitle="Coordonnées du bistrot" />
      <div className="bg-white border border-[#E5E0D8] p-8 space-y-4 max-w-2xl">
        <div><Label>Adresse</Label><Input value={form.adresse || ""} onChange={(e) => set("adresse", e.target.value)} className="rounded-none" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Téléphone</Label><Input value={form.telephone || ""} onChange={(e) => set("telephone", e.target.value)} className="rounded-none" /></div>
          <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="rounded-none" /></div>
        </div>
        <Button onClick={save} data-testid="settings-save-btn" className="bg-[#A61C24] hover:bg-[#8B151E] text-white rounded-none">Enregistrer</Button>
      </div>
    </div>
  );
}
